import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Job, JobType, PhotoSource } from "@eagledispatch/shared";
declare const BUILD_TAG: string;
import { apiFetch, ApiError } from "../../lib/api";
import { queuedApiFetch } from "../../lib/offline-queue";
import Button from "../../components/Button";
import Card from "../../components/Card";

/**
 * Upload state machine. The on-screen thumbnail (`previewUrl`) is NOT part
 * of this union — it is owned exclusively by pick()/retake() so that no
 * upload transition (or the mount-time server re-sync) can ever wipe the
 * preview. The thumbnail lives from capture until RETAKE or WHAT'S NEXT.
 */
type UploadState =
  | { status: "idle" } // no photo yet
  | { status: "uploading"; source: PhotoSource } // auto-upload in flight
  | { status: "uploaded" } // server-confirmed
  | { status: "failed"; source: PhotoSource }; // upload failed, retry

/** Format remaining seconds to mm:ss */
function formatCountdown(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * JobComplete — the IN_PROGRESS driver screen (the heart of the flow).
 *
 * Photo: TAKE PHOTO (capture) / CHOOSE FROM GALLERY map to the CAMERA /
 * GALLERY source flag. Gallery is SILENT (office sees the red flag on
 * review; no driver-facing warning). Per spec, the photo uploads
 * IMMEDIATELY on capture (offline-tolerance: the ticket reaches the server
 * the moment signal exists — a deferred upload could be lost when the
 * driver leaves coverage). Upload retries reuse the same idempotency key;
 * RETAKE restarts with a fresh key. The thumbnail stays visible through
 * upload and until the driver finishes or retakes.
 *
 * WHAT'S NEXT is disabled until the upload is server-confirmed. Finish
 * holds a single idempotency key so retries replay the same operation.
 *
 * E/R Return Cooldown (Unit #12.7):
 * For E/R jobs, WHAT'S NEXT remains locked during the cooldown window
 * (tenant er_return_minutes, default 45 min) anchoring at ticketPhotoAt.
 * A live mm:ss countdown is displayed prominently.
 *
 * Replay=ack contract (#11): on mount and after any finish failure,
 * re-sync via GET /jobs/:id. If the job is already FINISHED (a prior
 * finish landed but its response was lost), route straight to waiting.
 */
export default function JobComplete({
  jobId,
  jobNumber = "",
  jobType,
  erReturnMinutes = 45,
  initialTicketPhotoAt,
  currentDriverId,
  onBack,
  onFinished,
  onCancelled,
  onReassigned,
  onPaused,
}: {
  jobId: string;
  jobNumber?: string;
  jobType?: JobType | string;
  erReturnMinutes?: number;
  initialTicketPhotoAt?: string | null;
  currentDriverId: string;
  onBack?: () => void;
  onFinished: () => void;
  onCancelled: () => void;
  onReassigned: () => void;
  onPaused?: () => void;
}) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const [upload, setUpload] = useState<UploadState>({ status: "idle" });
  // Preview blob URL — see UploadState docblock. Mirror ref for cleanup.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewRef = useRef<string | null>(null);
  previewRef.current = previewUrl;

  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Held idempotency keys — retries reuse them (same operation, same key).
  const finishKeyRef = useRef<string | null>(null);
  const uploadKeyRef = useRef<string | null>(null);
  // The selected File (retry re-uploads the actual file, not a placeholder).
  const fileRef = useRef<File | null>(null);

  // SPEC-42: Engagement GPS tracking for D_O jobs
  const engagementGeoRef = useRef<{ lat: number; lng: number; accuracyM: number } | null>(null);
  const lastOpenSentNullsRef = useRef<boolean>(false);

  // Fire-once guards for the three terminal callbacks (Unit #14 SB-2).
  // syncJob is invoked from BOTH the mount-time useEffect AND the 30s poll
  // useEffect — two effects means closure-local flags in one effect are
  // invisible to the other. Refs are shared across effects and across
  // re-renders, so check-and-set BEFORE invoking guarantees each callback
  // fires at most once per mount. Once true, stays true for the lifetime
  // of the screen. The unmount teardown is irrelevant — the callbacks
  // route to the parent (which tears down JobComplete on navigation).
  const firedFinishedRef = useRef<boolean>(false);
  const firedCancelledRef = useRef<boolean>(false);
  const firedReassignedRef = useRef<boolean>(false);
  // Unit #25 final: explicit fire-once for the PAUSED branch.
  // Mirrors the firedFinishedRef / firedCancelledRef / firedReassignedRef
  // pattern — guards against double-firing from the mount + poll
  // useEffects both calling syncJob.
  const firedPausedRef = useRef<boolean>(false);

  // Cooldown anchor & tenant duration state
  const [photoAt, setPhotoAt] = useState<string | null>(initialTicketPhotoAt ?? null);
  const [cooldownMinutes, setCooldownMinutes] = useState<number>(erReturnMinutes);
  // Server-supplied retryAt override (for clock skew handling)
  const [serverRetryAt, setServerRetryAt] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  // Unit #25 send-back: explicit PAUSED branch. Dispatch paused this
  // job to redirect the driver to a higher-priority one. The driver's
  // local active-job endpoint will return null on next poll, so
  // ActiveJob routes them to WaitingScreen — but a job in the middle
  // of JobComplete (mid-photo) won't re-query ActiveJob; this state
  // handles the in-flight case. The job is NOT cancelled and NOT
  // reassigned — it is still this driver's, just paused. We disable
  // upload + finish and show a notice; the server 400 stays as the
  // backstop if a stale client somehow submits.
  const [pausedNotice, setPausedNotice] = useState<boolean>(false);

  // Tick every second when cooldown might be active
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  /**
   * Re-sync job state from the server (#11 replay=ack contract + #14 change
   * detection). If a prior finish already landed (FINISHED/REVIEWED), route
   * to waiting WITHOUT remounting; if the photo is already server-confirmed
   * (reload mid-job), reflect it WITHOUT touching the preview. If the job
   * was CANCELLED in the meantime, the cancellation wins over the cooldown
   * — route to waiting immediately. If the job's driverId is no longer
   * this driver's, the job was reassigned — route to waiting. Non-fatal
   * on network failure (slow-network tolerance).
   *
   * Fire-once guards (Unit #14 SB-2): this function is invoked from BOTH
   * a mount-time useEffect AND a 30s poll useEffect. A slow mount response
   * can overlap a poll response; without check-and-set, the terminal
   * callback could fire twice (double-routing, double-fetch in parent).
   * Each guard is set the instant the callback is invoked and stays set
   * for the lifetime of the mount — the unmount tears the screen down.
   */
  const syncJob = useCallback(async () => {
    try {
      const job = await apiFetch<Job>(`/api/v1/jobs/${jobId}`);
      // Unit #25 send-back: explicit PAUSED branch. Dispatch paused
      // this job mid-flow to redirect the driver to a priority job.
      // The job is still this driver's (driver_id unchanged), still
      // active in the sense that it'll be resumed — but the driver
      // is freed and any in-flight upload / finish would 400.
      //
      // Unit #25 final: invoke onPaused() exactly once via
      // firedPausedRef. The parent's preferred behavior is to
      // route to WaitingScreen automatically (the brief: "callback
      // is better for automatic behavior"). The brief also requires
      // the driver not to be stranded on a disabled screen; the
      // firedPausedRef guard prevents double-routing from the
      // mount + 30s poll useEffects both calling syncJob.
      if (job.state === "PAUSED") {
        setPausedNotice(true);
        if (firedPausedRef.current) return;
        firedPausedRef.current = true;
        if (onPaused) onPaused();
        return;
      }
      if (job.state === "FINISHED" || job.state === "REVIEWED") {
        if (firedFinishedRef.current) return;
        firedFinishedRef.current = true;
        onFinished();
        return;
      }
      // CANCELLED first — wins over a reassign (cancel sets driver_id NULL
      // too, but the screen should show "cancelled" not "reassigned").
      if (job.state === "CANCELLED") {
        if (firedCancelledRef.current) return;
        firedCancelledRef.current = true;
        onCancelled();
        return;
      }
      // Reassigned detection is null-inclusive. Reassign clears driver_id
      // to NULL — the very state the driver used to belong to this job
      // and no longer does. Without `!== null`, the driver would keep
      // uploading photos / trying to finish on a job that's no longer
      // theirs. The unmount teardown cancels any timers (cooldown /
      // upload retries) when the screen unmounts.
      if (job.driverId !== currentDriverId) {
        if (firedReassignedRef.current) return;
        firedReassignedRef.current = true;
        onReassigned();
        return;
      }
      if (job.ticketPhotoUrl) {
        setUpload((prev) =>
          prev.status === "uploaded" ? prev : { status: "uploaded" },
        );
        if (job.ticketPhotoAt) setPhotoAt(job.ticketPhotoAt);
        if (job.erReturnMinutes) setCooldownMinutes(job.erReturnMinutes);
      }
    } catch {
      // Network down — not proof of anything; keep current screen state.
    }
  }, [jobId, currentDriverId, onFinished, onCancelled, onReassigned, onPaused]);

  // ── Mount re-sync: recover from a lost finish response ──────────────────
  useEffect(() => {
    void syncJob();
    return () => {
      // Revoke the preview blob URL on unmount (finish/navigation).
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 30s poll (Unit #14 change detection) ───────────────────────────────
  // Mid-job the screen has no other way to learn that dispatch CANCELLED
  // or reassigned the job. Polling keeps the driver in sync. The brief is
  // explicit: cancellation wins over a running cooldown — `onCancelled`
  // routes to waiting immediately and the cooldown timer is torn down by
  // the unmount of the new screen.
  useEffect(() => {
    const POLL_MS = 30_000;
    const id = window.setInterval(() => void syncJob(), POLL_MS);
    return () => window.clearInterval(id);
  }, [syncJob]);

  const sendEngagementOpen = useCallback(
    async (geo: { lat: number; lng: number; accuracyM: number } | null) => {
      // Phase-1 audit wiring note: engagement-open requires IN_PROGRESS or
      // recently finished — skip if job is paused or we know it's not D_O.
      if (pausedNotice || jobType !== "D_O") return;
      const body = geo ? { lat: geo.lat, lng: geo.lng, accuracyM: geo.accuracyM } : {};
      lastOpenSentNullsRef.current = !geo;
      try {
        await apiFetch(`/api/v1/jobs/${jobId}/engagement-open`, {
          method: "POST",
          headers: {
            "Idempotency-Key": crypto.randomUUID(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
      } catch {
        // Best effort — finish tx has D_O ensure-row guarantee; office can drop pin later
      }
    },
    [jobId, jobType, pausedNotice],
  );

  const doUpload = useCallback(
    async (file: File, source: PhotoSource, key: string) => {
      // Unit #25 final: refuse to start an upload if dispatch has
      // paused the job. The server's IN_PROGRESS-only gate is the
      // backstop (and would 400 with "Can only upload a ticket
      // photo while IN_PROGRESS"), but we mark the local state as
      // canceled so the UI doesn't show a phantom "uploading…"
      // indicator that the server will never resolve.
      if (pausedNotice) {
        setUpload({ status: "failed", source });
        setError("Job paused by dispatch — upload canceled.");
        return;
      }
      setUpload({ status: "uploading", source });
      try {
        const form = new FormData();
        form.append("photo", file);
        form.append("source", source);
        const res = await apiFetch<{
          ticketPhotoUrl: string;
          photoSource: PhotoSource;
          photoAt?: string;
          erReturnMinutes?: number;
        }>(`/api/v1/jobs/${jobId}/ticket-photo`, {
          method: "POST",
          headers: { "Idempotency-Key": key },
          body: form,
        });
        // If pause arrived DURING the upload, the server may still
        // have accepted the request (the lock-and-guard serializes,
        // but a slow request that committed before the pause was
        // applied could return 200). Don't downgrade the local
        // pausedNotice state, and don't try to call finish() — the
        // screen is already on its way to WaitingScreen via
        // onPaused. The local upload result is informational only.
        if (pausedNotice) return;
        setUpload({ status: "uploaded" });
        if (res.photoAt) setPhotoAt(res.photoAt);
        if (res.erReturnMinutes) setCooldownMinutes(res.erReturnMinutes);
        setServerRetryAt(null);
        setError(null);

        // SPEC-42: On photo upload success, call engagement-open if D_O
        if (jobType === "D_O") {
          void sendEngagementOpen(engagementGeoRef.current);
        }
      } catch (e) {
        // If pause arrived during the upload, the server 400
        // ("Can only upload…") is the expected response — convert
        // it to a clean local cancel rather than a confusing
        // failure. The server gate is the source of truth.
        if (pausedNotice) {
          setUpload({ status: "failed", source });
          setError("Job paused by dispatch — upload canceled.");
          return;
        }
        setUpload({ status: "failed", source });
        setError(
          e instanceof ApiError || e instanceof Error
            ? e.message
            : String(e),
        );
      }
    },
    [jobId, jobType, pausedNotice, sendEngagementOpen],
  );

  const pick = (source: PhotoSource, files: FileList | null) => {
    // Unit #25 send-back: refuse upload if dispatch has paused the job.
    // The server would 400 anyway (photo route is IN_PROGRESS-only) —
    // this is the client-side backstop.
    if (pausedNotice) return;
    const file = files?.[0];
    if (!file) return;

    // SPEC-42: Best-effort GPS capture on D_O camera tap
    if (jobType === "D_O" && source === "CAMERA") {
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          engagementGeoRef.current = {
            lat: pos.coords.latitude, // raw number — precision lives in the DB column numeric(9,6)
            lng: pos.coords.longitude,
            accuracyM: Math.round(pos.coords.accuracy),
          };
          // SPEC-42 Late-fix retry: if previous open-call sent nulls, fire again with coords
          if (lastOpenSentNullsRef.current && engagementGeoRef.current) {
            void sendEngagementOpen(engagementGeoRef.current);
          }
        },
        () => {
          /* no fix / denied — best-effort, leave ref untouched */
        },
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
      );
    }

    // Retake-in-place: clear any previous preview before making the new one.
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    fileRef.current = file;
    // Fresh idempotency key per photo attempt; retries reuse it.
    const key = crypto.randomUUID();
    uploadKeyRef.current = key;
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
    // Spec: upload fires immediately on capture.
    void doUpload(file, source, key);
  };

  const retake = () => {
    // Unit #25 final: refuse to retake when the job is paused. The
    // server gate is the backstop (the photo route would 400); we
    // also don't want to wipe the in-flight preview state because
    // the driver is being routed to WaitingScreen and the
    // preview/upload state is irrelevant there. The button is also
    // disabled in render (defense-in-depth).
    if (pausedNotice) return;
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    setPreviewUrl(null);
    uploadKeyRef.current = null;
    fileRef.current = null;
    // Reset the file inputs so re-selecting the SAME file fires a change
    // event (an unchanged input value silently swallows the pick).
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
    setUpload({ status: "idle" });
    setError(null);
  };

  // Cooldown calculation (for E_R jobs)
  const remainingSeconds = useMemo(() => {
    if (jobType !== "E_R") return 0;
    // Target unlock moment: serverRetryAt if set (handles clock skew), else photoAt + cooldownMinutes
    let targetMs: number | null = null;
    if (serverRetryAt) {
      targetMs = new Date(serverRetryAt).getTime();
    } else if (photoAt) {
      targetMs = new Date(photoAt).getTime() + cooldownMinutes * 60 * 1000;
    }
    if (!targetMs) return 0;
    const diff = Math.ceil((targetMs - now) / 1000);
    return diff > 0 ? diff : 0;
  }, [jobType, serverRetryAt, photoAt, cooldownMinutes, now]);

  const isCooldownActive = jobType === "E_R" && upload.status === "uploaded" && remainingSeconds > 0;

  const finish = async () => {
    // Photo must be server-confirmed before a FINISH can ever be queued or
    // sent — the FINISHED gate requires ticket_photo_url, and we never queue
    // a FINISH without an uploaded photo (spec edge case).
    if (upload.status !== "uploaded") {
      setError("Photo still uploading — can't finish yet.");
      return;
    }
    setFinishing(true);
    setError(null);
    // Hold the key across retries.
    if (!finishKeyRef.current) finishKeyRef.current = crypto.randomUUID();
    try {
      // queuedBody carries queuedOffline:true on replay; the live attempt
      // sends an empty body. If offline, the queue stores the SAME key.
      const res = await queuedApiFetch(`/api/v1/jobs/${jobId}/finish`, {}, {
        idempotencyKey: finishKeyRef.current,
        jobId,
        jobNumber,
        actionType: "FINISH",
        queuedBody: { queuedOffline: true },
      });
      if (res.status === 202) {
        // Offline — action queued. Optimistic navigation; WaitingScreen shows
        // the "Finish queued" chip via the queue-status banner.
        onFinished();
        return;
      }
      onFinished();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const retryAt = e.retryAt;
        if (retryAt) {
          setServerRetryAt(retryAt);
          setError(`Return cooldown active until ${new Date(retryAt).toLocaleTimeString()}`);
          return;
        }
      }
      // replay=ack: the finish POST may have landed but its response was
      // lost (network blip). Re-sync WITHOUT remounting — if the job is now
      // FINISHED/REVIEWED, route straight to waiting; otherwise keep the
      // held key and let the driver retry (same operation, same key).
      setError(
        e instanceof ApiError || e instanceof Error
          ? e.message
          : String(e),
      );
      void syncJob();
    } finally {
      setFinishing(false);
    }
  };

  const canFinish = upload.status === "uploaded" && !finishing && !isCooldownActive && !pausedNotice;
  const busy = upload.status === "uploading" || finishing;

  const photoHeading = jobType === "D_O" ? "Delivery Photo" : "Ticket Photo";

  return (
    <main className="surface driver-complete">
      <header className="surface-header">
        {onBack ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button variant="ghost" onClick={onBack} style={{ padding: "4px 8px" }}>
              ← Job info
            </Button>
            <h1 style={{ margin: 0, fontSize: "20px" }}>Job Complete</h1>
          </div>
        ) : (
          <h1>Job Complete</h1>
        )}
        <div className="header-actions">
          <span className="muted build-stamp" title={`build ${BUILD_TAG}`}>
            {BUILD_TAG.slice(0, 7)}
          </span>
        </div>
      </header>

      <Card className="photo-section">
        <h2>{photoHeading}</h2>

        {pausedNotice && (
          <div
            className="paused-banner"
            role="status"
            aria-live="polite"
            style={{
              padding: "12px 14px",
              marginBottom: "12px",
              borderRadius: "8px",
              background: "rgba(232, 180, 80, 0.18)",
              border: "1px solid rgba(232, 180, 80, 0.5)",
              color: "#8A6D00",
            }}
          >
            <strong>Job paused by dispatch.</strong>{" "}
            Return to the waiting screen — you'll be notified when it's
            resumed. The active-job list will drop this job and assign
            you a new one in the meantime.
          </div>
        )}

        {upload.status === "idle" && (
          <div className="photo-actions">
            <input
              ref={cameraRef}
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              hidden
              disabled={pausedNotice}
              onChange={(e) => pick("CAMERA", e.target.files)}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/jpeg,image/png"
              hidden
              disabled={pausedNotice}
              onChange={(e) => pick("GALLERY", e.target.files)}
            />
            <Button
              variant="danger"
              disabled={pausedNotice}
              onClick={() => cameraRef.current?.click()}
            >
              📷 TAKE PHOTO
            </Button>
            <Button
              variant="secondary"
              disabled={pausedNotice}
              onClick={() => galleryRef.current?.click()}
            >
              🖼 Choose from Gallery
            </Button>
          </div>
        )}

        {upload.status !== "idle" && (
          <div className="photo-preview">
            {previewUrl && (
              <img
                className="photo-thumb"
                src={previewUrl}
                alt="Photo preview"
              />
            )}
            {upload.status === "uploading" && (
              <p className="muted">Uploading…</p>
            )}
            {upload.status === "failed" && (
              <>
                <p className="err">Upload failed — check your connection.</p>
                <Button
                  disabled={busy || pausedNotice}
                  onClick={() => {
                    if (fileRef.current) {
                      void doUpload(
                        fileRef.current,
                        upload.source,
                        uploadKeyRef.current ?? crypto.randomUUID(),
                      );
                    }
                  }}
                >
                  Retry Upload
                </Button>
              </>
            )}
            {upload.status === "uploaded" && (
              <p className="upload-ok">✓ Photo uploaded</p>
            )}
            {upload.status !== "uploading" && (
              <Button
                variant="ghost"
                disabled={pausedNotice}
                onClick={retake}
              >
                Retake
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* E/R Cooldown banner */}
      {isCooldownActive && (
        <Card className="cooldown-section">
          <p className="cooldown-title">
            Return the dumpster
          </p>
          <div className="cooldown-countdown">
            {formatCountdown(remainingSeconds)}
          </div>
          <p className="muted footnote cooldown-footnote">
            WHAT'S NEXT unlocks in {formatCountdown(remainingSeconds)}
          </p>
        </Card>
      )}

      {error && <p className="form-error">{error}</p>}

      <Button
        className="send-button finish-button"
        disabled={!canFinish}
        onClick={() => void finish()}
      >
        {finishing ? "Finishing…" : "WHAT'S NEXT"}
      </Button>
      <p className="muted footnote">
        {canFinish
          ? "Tap to finish the job."
          : isCooldownActive
          ? "Return drive cooldown in progress."
          : "Upload a photo to continue."}
      </p>
    </main>
  );
}
